import React, { useEffect, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import Layout from '../components/Layout';
import {
  apiError, ATTENDANCE_LABELS, AttendanceStatus, formatSessionDate, formatSessionWindow, SessionDetail, trainingService,
} from '../api/trainingService';
import {
  buttonClass, ErrorText, secondaryButtonClass, SessionForm, StatusPill, TrainerSlots, useTrainingRoles, VenuePicker,
} from '../components/training/TrainingShared';

const STATUSES: AttendanceStatus[] = ['present', 'late', 'excused', 'absent'];
const activeStyle: Record<AttendanceStatus, string> = {
  present: 'bg-green-600 text-white border-green-600',
  late: 'bg-lime-600 text-white border-lime-600',
  excused: 'bg-amber-500 text-white border-amber-500',
  absent: 'bg-red-600 text-white border-red-600',
};

const AttendanceTaker: React.FC<{ session: SessionDetail }> = ({ session }) => {
  const qc = useQueryClient();
  const rows = useQuery({ queryKey: ['training', 'attendance', session.id], queryFn: () => trainingService.attendance(session.id) });
  const [draft, setDraft] = useState<Record<number, AttendanceStatus | null>>({});
  useEffect(() => {
    if (rows.data) setDraft(Object.fromEntries(rows.data.map((r) => [r.enrollmentId, r.status])));
  }, [rows.data]);
  const dirty = (rows.data || []).some((r) => (draft[r.enrollmentId] ?? null) !== r.status);
  const save = useMutation({
    mutationFn: () => trainingService.saveAttendance(session.id, (rows.data || [])
      .filter((r) => (draft[r.enrollmentId] ?? null) !== r.status)
      .map((r) => ({ enrollmentId: r.enrollmentId, status: draft[r.enrollmentId] ?? null }))),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['training'] }),
  });
  const markAll = () => setDraft(Object.fromEntries((rows.data || []).map((r) => [r.enrollmentId, draft[r.enrollmentId] || 'present'])));

  return (
    <div className="bg-white rounded-xl shadow-sm p-5 space-y-3">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <h2 className="font-semibold text-lg">Attendance</h2>
        <button className="text-sm text-primary-700 hover:underline" onClick={markAll}>Mark everyone else present</button>
      </div>
      {rows.isLoading && <p className="text-sm text-gray-600">Loading…</p>}
      <ErrorText error={rows.error} />
      {rows.data?.length === 0 && <p className="text-sm text-gray-600">No planters are enrolled.</p>}
      <ul className="divide-y divide-gray-100">
        {(rows.data || []).map((r) => (
          <li key={r.enrollmentId} className="py-3 flex flex-col sm:flex-row sm:items-center gap-2">
            <span className="font-medium flex-1">{r.planter.name}{r.planter.spouseName ? ` & ${r.planter.spouseName}` : ''}</span>
            <div className="flex gap-1" role="group" aria-label={`Attendance for ${r.planter.name}`}>
              {STATUSES.map((s) => {
                const on = draft[r.enrollmentId] === s;
                return (
                  <button key={s} aria-pressed={on}
                    className={`px-3 py-1.5 text-sm rounded-lg border ${on ? activeStyle[s] : 'border-gray-300 text-gray-700 bg-white hover:bg-gray-50'}`}
                    onClick={() => { save.reset(); setDraft({ ...draft, [r.enrollmentId]: on ? null : s }); }}>
                    {ATTENDANCE_LABELS[s]}
                  </button>
                );
              })}
            </div>
          </li>
        ))}
      </ul>
      <div className="flex items-center gap-3">
        <button className={buttonClass} disabled={!dirty || save.isPending} onClick={() => save.mutate()}>{save.isPending ? 'Saving…' : 'Save attendance'}</button>
        {save.isSuccess && !dirty && <span className="text-sm text-green-700">Saved</span>}
      </div>
      <ErrorText error={save.error} fallback="Unable to save attendance" />
    </div>
  );
};

const RemindButtons: React.FC<{ session: SessionDetail }> = ({ session }) => {
  const [message, setMessage] = useState<string | null>(null);
  const remind = useMutation({
    mutationFn: (audience: 'all' | 'trainers' | 'planters') => trainingService.remind(session.id, audience),
    onSuccess: (r) => setMessage(`Sent ${r.sent}${r.failed ? `, ${r.failed} not sent (${r.reasons.join('; ')})` : ''}.`),
    onError: (e) => setMessage(apiError(e)),
  });
  const send = (audience: 'all' | 'trainers' | 'planters', label: string) => {
    if (window.confirm(`Email a reminder for this session to ${label} now?`)) remind.mutate(audience);
  };
  return (
    <div className="bg-white rounded-xl shadow-sm p-5 space-y-2">
      <h2 className="font-semibold">Send a reminder now</h2>
      <p className="text-xs text-gray-500">Automatic reminders already go out: trainers 14 and 2 days before, planters 7 days and 1 day before.</p>
      <div className="flex flex-wrap gap-2">
        <button className={secondaryButtonClass} disabled={remind.isPending || session.semester.status !== 'published'} onClick={() => send('planters', 'enrolled planters')}>Planters</button>
        <button className={secondaryButtonClass} disabled={remind.isPending || session.semester.status !== 'published'} onClick={() => send('trainers', 'the trainers')}>Trainers</button>
        <button className={secondaryButtonClass} disabled={remind.isPending || session.semester.status !== 'published'} onClick={() => send('all', 'everyone')}>Everyone</button>
      </div>
      {session.semester.status !== 'published' && <p className="text-xs text-gray-500">Publish the semester to send reminders.</p>}
      {message && <p className="text-sm text-gray-700">{message}</p>}
    </div>
  );
};

const TrainingSessionPage: React.FC = () => {
  const { sessionId } = useParams();
  const id = Number(sessionId);
  const navigate = useNavigate();
  const qc = useQueryClient();
  const { isStaff } = useTrainingRoles();
  const [editing, setEditing] = useState(false);
  const session = useQuery({ queryKey: ['training', 'session', id], queryFn: () => trainingService.session(id) });
  const refresh = () => qc.invalidateQueries({ queryKey: ['training'] });
  const update = useMutation({
    mutationFn: (input: Parameters<typeof trainingService.updateSession>[1]) => trainingService.updateSession(id, input),
    onSuccess: () => { refresh(); setEditing(false); },
  });
  const remove = useMutation({ mutationFn: () => trainingService.deleteSession(id), onSuccess: () => navigate(`/training/${session.data?.semesterId}`) });
  const s = session.data;

  return (
    <Layout>
      <div className="space-y-5 max-w-5xl">
        {s && <Link to={`/training/${s.semesterId}`} className="text-sm text-primary-700 hover:underline">← {s.semester.name}</Link>}
        {session.isLoading && <p className="text-gray-600">Loading…</p>}
        <ErrorText error={session.error} fallback="Unable to load session" />
        {s && (
          <>
            <div className="bg-white rounded-xl shadow-sm p-5 space-y-4">
              <div className="flex flex-wrap items-start gap-3">
                <div className="flex-1 min-w-0">
                  <h1 className="text-2xl font-bold text-gray-900">{s.displayTitle}</h1>
                  <p className="text-gray-700">{formatSessionDate(s.startsAt, s.timezone)} · {formatSessionWindow(s.startsAt, s.endsAt, s.timezone)} Central</p>
                </div>
                {s.status === 'cancelled' && <StatusPill status="cancelled" />}
                {s.permissions.canEdit && !editing && (
                  <div className="flex gap-2">
                    <button className={secondaryButtonClass} onClick={() => setEditing(true)}>Edit session</button>
                    {s.status === 'scheduled'
                      ? <button className={secondaryButtonClass} onClick={() => window.confirm(s.semester.status === 'published' ? 'Cancel this session? Trainers and planters will be emailed.' : 'Cancel this session?') && update.mutate({ status: 'cancelled' })}>Cancel session</button>
                      : <button className={secondaryButtonClass} onClick={() => update.mutate({ status: 'scheduled' })}>Restore</button>}
                    {s.semester.status === 'draft' && <button className="text-sm text-red-600 hover:underline" onClick={() => window.confirm('Delete this session?') && remove.mutate()}>Delete</button>}
                  </div>
                )}
              </div>

              {editing ? (
                <SessionForm initial={s} defaultStartTime={s.semester.defaultStartTime} defaultDurationMinutes={s.semester.defaultDurationMinutes}
                  submitLabel="Save session" pending={update.isPending} error={update.error}
                  onSubmit={(input) => update.mutate(input)} onCancel={() => setEditing(false)} />
              ) : (
                <>
                  <div>
                    <h2 className="text-sm font-semibold text-gray-700 mb-1">Location</h2>
                    {s.permissions.canChangeVenue ? (
                      <VenuePicker value={s.venue?.id ?? null} onChange={(venueId) => update.mutate({ venueId })} disabled={update.isPending} />
                    ) : (
                      <p className="text-sm text-gray-700">
                        {s.venue ? <>{s.venue.name}{s.venue.address ? ` · ${s.venue.address}` : ''}{s.venue.mapUrl && <> · <a className="text-primary-700 underline" href={s.venue.mapUrl} target="_blank" rel="noreferrer">Map</a></>}</> : 'Location to be announced'}
                      </p>
                    )}
                    {s.venue?.notes && <p className="text-sm text-gray-500 mt-1">{s.venue.notes}</p>}
                    {s.permissions.canChangeVenue && s.semester.status === 'published' && (
                      <p className="text-xs text-gray-500 mt-1">Changing the venue emails the trainers and enrolled planters an updated calendar invite.</p>
                    )}
                  </div>
                  {s.outcomes.length > 0 && (
                    <div>
                      <h2 className="text-sm font-semibold text-gray-700 mb-1">Outcomes</h2>
                      <ul className="text-sm text-gray-700 list-disc pl-5">
                        {s.outcomes.map((o, i) => <li key={o.id ?? i} className={o.isDraft ? 'italic text-gray-400' : ''}>{o.text}{o.isDraft ? ' (draft — hidden from planters)' : ''}</li>)}
                      </ul>
                    </div>
                  )}
                  {s.notes && <p className="text-sm italic text-gray-600">{s.notes}</p>}
                  <div>
                    <h2 className="text-sm font-semibold text-gray-700 mb-1">Trainers</h2>
                    <TrainerSlots session={s} canAssign={isStaff} />
                    {!isStaff && !s.permissions.canReleaseOwnSlot && s.trainers.some((t) => t.trainer && s.permissions.canTakeAttendance) && (
                      <p className="text-xs text-gray-500 mt-1">Within {s.permissions.releaseCutoffDays} days of the session, contact staff to release your slot.</p>
                    )}
                  </div>
                  {s.semester.trainerManualUrl && (
                    <a className="text-sm text-primary-700 hover:underline" href={s.semester.trainerManualUrl} target="_blank" rel="noreferrer">Open the trainer manual</a>
                  )}
                </>
              )}
              <ErrorText error={!editing ? update.error : null} />
              <ErrorText error={remove.error} />
            </div>

            {s.permissions.canTakeAttendance && s.status !== 'cancelled' && <AttendanceTaker session={s} />}
            {isStaff && <RemindButtons session={s} />}
          </>
        )}
      </div>
    </Layout>
  );
};

export default TrainingSessionPage;
